"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { PhoneCall, ShieldCheck, Hammer, FileText, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
const faqItems = [{
  id: "rebuild-timeline",
  question: "How quickly can reconstruction begin once disaster mitigation and drying are completed?",
  answer: "Reconstruction planning starts while active drying is still underway. Once our technicians take final moisture readings that verify framing and subfloors meet dry standards, your dedicated project manager completes a full reconstruction walk-through within 24 to 48 hours. Because our local in-house teams handle both mitigation and structural rebuilding under one roof, you bypass the multi-week handoff delays common when hiring separate contractors.",
  badge: "Timeline & Crew Dispatch"
}, {
  id: "layout-modifications",
  question: "Can homeowners make custom layout modifications or upgrade material finishes during the rebuild?",
  answer: "Yes. A rebuild is often an ideal moment to remove non-load-bearing partition walls, reconfigure kitchen islands, or upgrade from contractor-grade laminate to quartz counters and solid hardwood. Our design-build estimators separate your insurance-approved base scope from your chosen upgrades on clear line-item sheets, so you always know your exact out-of-pocket differential before materials are ordered.",
  badge: "Custom Finishes & Design"
}, {
  id: "permits-inspections",
  question: "Who handles local municipal building permits and code inspections?",
  answer: "TriServe manages every phase of local permitting and municipal inspections across Maryland, Washington DC, and Northern Virginia jurisdictions. Our licensed trade leads prepare structural drawings, submit documentation to local county offices, schedule required rough-in checks (electrical, plumbing, mechanical), and secure your final certificate of occupancy before final sign-off.",
  badge: "Jurisdiction & Code Compliance"
}, {
  id: "insurance-reconciliation",
  question: "How does TriServe ensure the rebuild scope matches insurance adjusters' line-item payouts?",
  answer: "We build every rebuild estimate using Xactimate, the same industry-standard pricing database used by major insurance carriers. When adjuster line items fall short of local building code requirements or current regional labor rates, our insurance coordinators submit documented photo evidence, moisture logs, and code citations directly to your adjuster to secure necessary supplements before construction starts.",
  badge: "Direct Claims Coordination"
}];
export default function Faqs() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 12
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: "easeOut"
      }
    }
  };
  return <section id="faqs" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-t border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Technical Spec Bar */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border pb-4 mb-10 text-xs font-mono tracking-wide text-muted-foreground uppercase">
          <div className="flex items-center gap-2">
            <span className="size-2 rounded-full bg-primary animate-pulse" />
            <span>Operational Standard // Reconstruction Scope & Permitting</span>
          </div>
          <div className="flex items-center gap-4">
            <span>Direct Crews • MD / DC / NOVA</span>
            <span className="hidden sm:inline text-border">|</span>
            <span className="hidden sm:inline">Xactimate Line-Item Billing</span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left Column: Heading & Reassurance Card */}
          <motion.div className="lg:col-span-5 flex flex-col justify-between space-y-8" initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} variants={itemVariants}>
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-bold uppercase tracking-wider">
                <Hammer className="size-3.5" />
                <span>Rebuild Clarifications</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground">
                Frequently asked rebuild questions
              </h2>
              <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
                Clear answers on project timing, municipal permits, insurance scope reconciliation, and custom finish upgrades before framing begins.
              </p>
            </div>

            {/* Direct Support Card */}
            <div className="rounded-lg border border-border bg-muted/40 p-6 space-y-5">
              <div className="flex items-start gap-3.5">
                <div className="p-2.5 rounded-md bg-primary/10 text-primary shrink-0">
                  <ShieldCheck className="size-5" />
                </div>
                <div className="space-y-1">
                  <h3 className="text-base font-semibold text-foreground">
                    Have specific blueprints or adjusters on site?
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Our local superintendents walk active loss sites across Maryland, DC, and Northern Virginia to verify structural integrity and permit parameters.
                  </p>
                </div>
              </div>

              <div className="pt-2 border-t border-border flex flex-col sm:flex-row sm:items-center gap-3">
                <Button asChild variant="default" className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold">
                  <a href="tel:8554356631">
                    <PhoneCall className="size-4" />
                    <span>Call 855-435-6631</span>
                  </a>
                </Button>

                <Button asChild variant="outline" className="border-border text-foreground hover:bg-muted">
                  <Link to="/ContactRequestServicePage">
                    <span>Submit Plans</span>
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </motion.div>

          {/* Right Column: Accordion Panels */}
          <motion.div className="lg:col-span-7" initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.15
        }} variants={containerVariants}>
            <Accordion type="single" collapsible defaultValue="rebuild-timeline" className="w-full space-y-3">
              {faqItems.map((item, i) => <motion.div key={item.id} data-index={i} variants={itemVariants} className="rounded-lg border border-border bg-card transition-[border-color,box-shadow] hover:border-primary/40 shadow-xs">
                  <AccordionItem value={item.id} className="border-b-0 px-5 sm:px-6">
                    <AccordionTrigger className="py-5 text-left font-semibold text-foreground hover:no-underline text-base sm:text-lg group">
                      <div className="flex flex-col gap-1 pr-4">
                        <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground font-normal">
                          {item.badge}
                        </span>
                        <span className="group-hover:text-primary transition-colors">
                          {item.question}
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground text-sm sm:text-base leading-relaxed pb-5 pt-1 border-t border-border/60 mt-1">
                      <div className="space-y-3">
                        <p>{item.answer}</p>
                        <div className="flex items-center gap-2 text-xs font-medium text-primary">
                          <FileText className="size-3.5" />
                          <span>Direct local crew accountability — No broker handoffs</span>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </motion.div>)}
            </Accordion>
          </motion.div>
        </div>
      </div>
    </section>;
}